import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import { filaPizzasService } from "../../../services/filaPizzasService";

type TipoFila = "doce" | "salgada" | "salao_doce" | "salao_salgada";

interface FilaItemObservacaoModalProps {
  isOpen: boolean;
  onClose: () => void;
  tipo: TipoFila;
  filaId: number;
  descricao: unknown;
  onSaved: () => void;
}

function asDescricaoArray(desc: unknown): { observacao?: string | null }[] {
  if (Array.isArray(desc)) return desc as { observacao?: string | null }[];
  if (desc && typeof desc === "object")
    return [desc as { observacao?: string | null }];
  return [];
}

export const FilaItemObservacaoModal: React.FC<
  FilaItemObservacaoModalProps
> = ({ isOpen, onClose, tipo, filaId, descricao, onSaved }) => {
  const [itemIndex, setItemIndex] = useState(0);
  const [texto, setTexto] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) setItemIndex(0);
  }, [isOpen, filaId, tipo]);

  useEffect(() => {
    if (!isOpen) return;
    const itens = asDescricaoArray(descricao);
    const safeIdx = Math.min(itemIndex, Math.max(0, itens.length - 1));
    const obs = itens[safeIdx]?.observacao;
    setTexto(obs != null ? String(obs) : "");
  }, [isOpen, itemIndex, descricao]);

  if (!isOpen) return null;

  const itens = asDescricaoArray(descricao);

  const handleSave = async () => {
    if (itens.length === 0) {
      toast.error("Não há itens na descrição desta fila.");
      return;
    }
    setSaving(true);
    try {
      const itensNow = asDescricaoArray(descricao);
      const idx = Math.min(itemIndex, Math.max(0, itensNow.length - 1));
      await filaPizzasService.patchItemObservacao(
        tipo,
        filaId,
        idx,
        texto.trim() === "" ? null : texto.trim()
      );
      toast.success("Observação salva.");
      onSaved();
      onClose();
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "message" in e
          ? String((e as { message?: string }).message)
          : "Erro ao salvar.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="relative w-full max-w-md rounded-lg bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 rounded-full p-1 text-neutral-500 hover:bg-neutral-100"
          aria-label="Fechar"
        >
          <X size={20} />
        </button>
        <h2 className="pr-10 text-lg font-semibold text-neutral-800">
          Observação da pizza
        </h2>
        <p className="mt-1 text-xs text-neutral-500">
          Fica salva no item da fila (integração / montagem).
        </p>

        {itens.length > 1 && (
          <div className="mt-4">
            <label className="mb-1 block text-xs font-medium text-neutral-600">
              Qual pizza?
            </label>
            <select
              value={itemIndex}
              onChange={(e) =>
                setItemIndex(Number.parseInt(e.target.value, 10))
              }
              className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
            >
              {itens.map((_, i) => (
                <option key={i} value={i}>
                  Pizza {i + 1}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="mt-4">
          <label className="mb-1 block text-xs font-medium text-neutral-600">
            Observação
          </label>
          <textarea
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            rows={4}
            className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm text-neutral-800 placeholder:text-neutral-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder="Ex.: Retirar a cebola"
          />
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60"
          >
            {saving ? "Salvando…" : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
};
